import {
	Badge,
	Button,
	Card,
	CartIcon,
	cn,
	Dialog,
	IconButton,
	XIcon,
} from "@au-schein-de/au-ui-library";
import type { PropsWithChildren } from "react";
import { useLoaderData, useRevalidator } from "react-router";
import type { Disclosure } from "~/hooks/use-disclosure";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { navigationClickedEvent } from "~/tooling/telemetry/events";
import type { Route } from "../../routes/+types/_storefront";

/** Figma 483 — close on green header (node 85:6333). */
const mobileCloseButtonClassName =
	"!size-10 !min-h-10 !min-w-10 !rounded-full !bg-ansay-400 hover:!bg-[#358f81] !text-white [&_svg]:!text-white";

interface CartWrapperProps extends PropsWithChildren {
	count: number;
	vendorName: string;
	amount: string;
	cartDisclosure: Disclosure;
	desktopIconOnly?: boolean;
	mobileIconOnly?: boolean;
	truncateVendorName?: boolean;
	renderDialog?: boolean;
}

export function CartWrapper({
	count,
	vendorName,
	amount,
	cartDisclosure,
	desktopIconOnly = false,
	mobileIconOnly = false,
	truncateVendorName = false,
	renderDialog = true,
	children,
}: CartWrapperProps) {
	const { stockValidationFlagEnabled } =
		useLoaderData<Route.ComponentProps["loaderData"]>();

	const revalidator = useRevalidator();

	const handleCartButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		const elementId = e.currentTarget.id;
		captureEvent(navigationClickedEvent(elementId, "", "main"));
		stockValidationFlagEnabled && revalidator.revalidate();
		cartDisclosure.toggle();
	};

	const desktopButton = desktopIconOnly ? (
		<div className="relative hidden md:flex">
			<IconButton
				id="cart-button"
				data-testid="cart-button"
				aria-label="Open cart"
				icon={<CartIcon.Raw />}
				iconSize={{ base: 5, md: 6 }}
				shape="square"
				className={`hidden md:flex !transition-colors ${cartDisclosure.isOpen ? "!bg-gray-400" : "bg-ansay-light hover:bg-ansay-light text-gray-700"}`}
				onClick={handleCartButtonClick}
			/>
			<Badge
				size="xs"
				variant="circle"
				className="absolute top-[2px] right-[2px] size-[22px] bg-gray-700 text-[13px]"
			>
				{count}
			</Badge>
		</div>
	) : (
		<Button
			id="cart-button"
			data-testid="cart-button"
			onClick={handleCartButtonClick}
			size="md"
			className="px-4 hidden md:flex"
		>
			<span className="flex items-center gap-2">
				<span className="flex items-center gap-1">
					<CartIcon size={6} />
					<Badge
						size="xs"
						variant="circle"
						className="size-[20px] rounded-[43px] bg-gray-700"
					>
						{count}
					</Badge>
				</span>
				<span
					className={
						truncateVendorName
							? "inline-block max-w-[125px] truncate text-body-emphasis"
							: "flex text-body-emphasis"
					}
				>
					{vendorName}
				</span>
				<span className="flex text-body" suppressHydrationWarning>
					{amount}
				</span>
			</span>
		</Button>
	);

	const mobileButton = cartDisclosure.isOpen ? (
		<div className="relative md:hidden">
			<IconButton
				id="cart-button-mobile"
				onClick={handleCartButtonClick}
				icon={<XIcon.Raw />}
				shape="circle"
				iconSize={5}
				aria-label="Close cart"
				className={mobileCloseButtonClassName}
				data-testid="close-cart-button"
			/>
		</div>
	) : mobileIconOnly ? (
		<div className="relative md:hidden">
			<IconButton
				id="cart-button-mobile"
				data-testid="cart-button-mobile"
				aria-label="Open cart"
				icon={<CartIcon.Raw />}
				iconSize={{ base: 5, md: 6 }}
				shape="square"
				className="md:hidden !transition-colors bg-ansay-light hover:bg-ansay-light text-gray-700"
				onClick={handleCartButtonClick}
			/>
			<Badge
				size="xs"
				variant="circle"
				className="absolute top-[2px] right-[2px] size-[18px] bg-gray-700 text-[11px]"
			>
				{count}
			</Badge>
		</div>
	) : (
		<div className="relative md:hidden">
			<IconButton
				id="cart-button-mobile"
				data-testid="cart-button-mobile"
				icon={<CartIcon.Raw />}
				shape="square"
				color="primary"
				onClick={handleCartButtonClick}
			/>
			<Badge
				size="xs"
				variant="circle"
				className="absolute top-1 right-1 size-5 bg-gray-700"
			>
				{count}
			</Badge>
		</div>
	);

	const emptyCartClassName = cn(
		"!transition-colors",
		cartDisclosure.isOpen && "!bg-gray-400",
		desktopIconOnly && "hidden md:flex",
		mobileIconOnly && "md:hidden",
	);

	return (
		<div className="relative z-(--z-index-cart-dialog) select-none">
			{count > 0 ? (
				<>
					{desktopButton}
					{mobileButton}
				</>
			) : (
				<IconButton
					id="cart-button"
					icon={<CartIcon.Raw />}
					iconSize={{ base: 5, md: 6 }}
					shape="square"
					data-testid="cart-button"
					className={emptyCartClassName}
					onClick={handleCartButtonClick}
				/>
			)}

			{renderDialog && (
				<Dialog.Root
					open={cartDisclosure.isOpen}
					onOpenChange={cartDisclosure.toggle}
				>
					<Dialog.Content
						variant="cart"
						withCloseButton={false}
						onFocusCapture={(event) => event.stopPropagation()}
						aria-describedby={undefined}
						mobileVariant="fullWidth"
						className="!top-0 md:!top-(--storefront-cart-offset-desktop) !bottom-0 md:!bottom-auto !rounded-none md:!rounded-xl"
					>
						<Dialog.Title className="hidden">Cart</Dialog.Title>
						<Card.Root
							variant="fullSpace"
							className="shadow-lg !rounded-none md:!rounded-xl !py-0"
						>
							<div className="h-dvh md:h-auto md:max-h-[calc(100dvh-var(--storefront-cart-content-offset-desktop))] md:py-6 flex flex-col md:gap-4">
								{children}
							</div>
						</Card.Root>
					</Dialog.Content>
				</Dialog.Root>
			)}
		</div>
	);
}
